"use client";
import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { Controller } from "react-hook-form"; // Add missing import
import { toast } from "react-toastify";

import { clearFieldErrors } from "@/lib/redux/slices/formSlice";
import getNestedError from "@/lib/utils/getNestedError";

const getColourStyles = (
  minHeight = "46px",
  isDisabled = false,
  padding = { paddingLeft: "1rem", paddingRight: "1rem" }
) => ({
  control: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: isDisabled ? "#f3f4f6" : "white",
    borderRadius: "0.5rem",
    borderColor: isFocused ? "#D9D9D9" : "#D9D9D9",
    "--tw-ring-opacity": "1",
    "--tw-ring-color": "rgb(93 69 239 / var(--tw-ring-opacity, 1))",
    "--tw-ring-offset-shadow":
      "var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
    "--tw-ring-shadow":
      "var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)",
    boxShadow: isFocused
      ? "var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)"
      : "none",
    minHeight: minHeight,
    height: minHeight,
    paddingLeft: padding.paddingLeft,
    paddingRight: padding.paddingRight,
    ":hover": {
      borderColor: isDisabled ? "#D9D9D9" : (isFocused ? "#883DF2" : "#D9D9D9"),
    },
    ".select__value-container": {
      paddingLeft: "1rem",
      paddingRight: "1rem",
    },
    ".select__indicators": {
      ".select__indicator-separator": {
        display: "none",
      },
    },
  }),
  placeholder: (styles) => ({
    ...styles,
    color: "#9ca3af", // grey-400 for placeholder
  }),
  singleValue: (styles, { data }) => ({
    ...styles,
    // If it's the default option (empty value), show in grey, otherwise black
    color: data?.value === "" || data?.value === null || data?.value === undefined 
      ? "#9ca3af" // grey-400 for default option
      : "#000000", // black for selected value
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? "#883DF2" : "white",
    color: isSelected ? "white" : "black",
    cursor: isDisabled ? "not-allowed" : "pointer",
    ":active": {
      ...styles[":active"],
      backgroundColor: "#883DF2",
      color: "white",
    },
    ":hover": {
      backgroundColor: "#883DF2",
      color: "white",
    },
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "red",
  }),

  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: "white",
    paddingRight: data?.isFixed ? 8 : styles.paddingRight,
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: "white",
    display: data?.isFixed ? "none" : "flex",
    ":hover": {
      backgroundColor: "red",
      color: "white",
    },
  }),
});

function DropdownField({
  label,
  name,
  options,
  register,
  control,
  errors,
  clearErrors,
  maxAllow,
  className,
  placeholder,
  isMulti = false,
  isRequired = false,
  defaultOptionLabel = "Select...",
  labelPosition = "top",
  fieldHeight = "medium",
  fieldWidth = undefined, // undefined = full width (for form builder), or "small"/"medium"/"large" for customer form
  fieldAlignment = "left", // "left", "center", or "right"
  isDisabled = false,
  disabled = false,
  labelColor = null,
  ...props
}) {
  const dispatch = useDispatch();

  const errorMessage = getNestedError(errors, name);

  // Get height for react-select based on fieldHeight prop - must match InputField
  const getSelectHeight = () => {
    switch (fieldHeight) {
      case "small":
        return "36px";
      case "large":
        return "56px";
      case "medium":
      default:
        return "46px";
    }
  };

  // Get padding classes to match InputField exactly
  const getSelectPadding = () => {
    switch (fieldHeight) {
      case "small":
        return { paddingLeft: "0.75rem", paddingRight: "0.75rem" };
      case "large":
        return { paddingLeft: "1rem", paddingRight: "1rem" };
      case "medium":
      default:
        return { paddingLeft: "1rem", paddingRight: "1rem" };
    }
  };

  // Get width classes based on fieldWidth and fieldAlignment props
  // Only apply width restrictions if fieldWidth is explicitly provided
  // If not provided (form builder), use full width without restrictions
  const getWidthClasses = () => {
    if (!fieldWidth || fieldWidth === undefined) {
      return "w-full"; // Full width for form builder
    }
    
    // Get alignment margin class
    const alignmentMargin = fieldAlignment === "center" ? "mx-auto" : fieldAlignment === "right" ? "ml-auto" : "";
    
    switch (fieldWidth) {
      case "small":
        return `w-full max-w-sm ${alignmentMargin}`;
      case "large":
        return "w-full"; // Full width (matches container width)
      case "medium":
      default:
        return `w-full max-w-xl ${alignmentMargin}`;
    }
  };

  const selectHeight = getSelectHeight();
  const selectPadding = getSelectPadding();
  const widthClasses = getWidthClasses();

  // Controlled filter text: react-select does not clear the search input when the clear (×) control
  // is used (it only clears the selected value). Keeping inputValue in sync fixes that.
  const [selectInputValue, setSelectInputValue] = React.useState("");

  // Combine both disabled props - if either is true, the field is disabled
  const isFieldDisabled = isDisabled || disabled;

  // Determine label rendering based on labelPosition
  const shouldShowLabel = label && labelPosition !== "placeholder";
  const shouldShowLabelTop = shouldShowLabel && labelPosition === "top";
  const shouldShowLabelLeft = shouldShowLabel && labelPosition === "left";
  // Only show placeholder when label position is "placeholder", otherwise no placeholder
  const placeholderText = labelPosition === "placeholder" && label ? label : (labelPosition === "placeholder" ? placeholder : "");
  
  // When labelPosition is "placeholder", use the placeholder text as defaultOptionLabel
  // Otherwise use the provided defaultOptionLabel or "Select..."
  const effectiveDefaultOptionLabel = labelPosition === "placeholder" && placeholderText 
    ? placeholderText 
    : (labelPosition === "placeholder" && placeholder 
      ? placeholder 
      : defaultOptionLabel);

  // Create options with default option for non-multi selects
  const enhancedOptions = React.useMemo(() => {
    if (isMulti) {
      return options || [];
    }
    
    // Add default option at the beginning for single select
    const defaultOption = {
      value: "",
      label: effectiveDefaultOptionLabel,
    };
    
    // Check if default option already exists in options
    const hasDefaultOption = options?.some(opt => opt.value === "" || opt.value === null);
    
    if (!hasDefaultOption) {
      return [defaultOption, ...(options || [])];
    }
    
    return options || [];
  }, [options, isMulti, effectiveDefaultOptionLabel]);

  const labelStyle = labelColor ? { color: labelColor } : { color: '#374151' }; // default text-gray-700

  return (
    <div className={`${widthClasses}`}>
      {shouldShowLabelTop && (
        <label className="block text-sm sm:text-base mb-2 sm:mb-2.5" style={labelStyle} htmlFor={name}>
          {label}
        </label>
      )}
      <div className={shouldShowLabelLeft ? "flex items-center gap-3" : ""}>
        {shouldShowLabelLeft && (
          <label className="block text-sm sm:text-base whitespace-nowrap min-w-[120px]" style={labelStyle} htmlFor={name}>
            {label}
          </label>
        )}
        <div className={shouldShowLabelLeft ? "flex-1 min-w-0" : ""}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <>
                <Select
              placeholder={placeholderText || placeholder || "Select..."}
              {...register(name)}
              {...field}
              id={name}
              options={enhancedOptions}
              isMulti={isMulti}
              isClearable={!isMulti} // Enable clear button for single select
              isDisabled={isFieldDisabled}
              inputValue={selectInputValue}
              onInputChange={(newValue) => {
                setSelectInputValue(newValue ?? "");
              }}
              styles={getColourStyles(selectHeight, isFieldDisabled, selectPadding)}
              onChange={(selectedOption, actionMeta) => {
                if (isFieldDisabled) {
                  return; // Prevent changes when disabled
                }
                if (actionMeta?.action === "clear") {
                  setSelectInputValue("");
                }
                if (isMulti) {
                  // Keep fixed/locked options from being removed.
                  const previousValues = Array.isArray(field.value)
                    ? field.value
                    : [];
                  const fixedValues = (enhancedOptions || [])
                    .filter(
                      (option) =>
                        option?.isFixed && previousValues.includes(option.value)
                    )
                    .map((option) => option.value);

                  let nextOptions = selectedOption || [];
                  if (
                    actionMeta?.action === "remove-value" ||
                    actionMeta?.action === "pop-value"
                  ) {
                    const removedValue = actionMeta?.removedValue?.value;
                    if (
                      actionMeta?.removedValue?.isFixed ||
                      fixedValues.includes(removedValue)
                    ) {
                      toast.warning(
                        "This location cannot be removed because classes were booked/attended there."
                      );
                      nextOptions = [
                        ...nextOptions,
                        actionMeta.removedValue,
                      ].filter(
                        (option, index, arr) =>
                          arr.findIndex((item) => item.value === option.value) ===
                          index
                      );
                    }
                  }

                  if (actionMeta?.action === "clear") {
                    nextOptions = (enhancedOptions || []).filter(
                      (option) =>
                        option?.isFixed && previousValues.includes(option.value)
                    );
                    if (nextOptions.length > 0) {
                      toast.warning(
                        "Some locations cannot be removed because classes were booked/attended there."
                      );
                    }
                  }

                  // Check if maxAllow limit is reached
                  if (
                    maxAllow &&
                    nextOptions &&
                    nextOptions.length > maxAllow
                  ) {
                    // Show toast message
                    toast.warning(
                      `You can select up to ${maxAllow} options only`
                    );
                    // Limit selections to maxAllow
                    const limitedSelection = nextOptions.slice(0, maxAllow);
                    const values = Array.from(
                      new Set([
                        ...fixedValues,
                        ...limitedSelection.map((option) => option.value),
                      ])
                    );
                    field.onChange(values);
                  } else {
                    const values = Array.from(
                      new Set([
                        ...fixedValues,
                        ...(nextOptions
                          ? nextOptions.map((option) => option.value)
                          : []),
                      ])
                    );
                    field.onChange(values);
                  }
                } else {
                  // Handle single select
                  // If default option (empty value) is selected, set to null
                  const value = selectedOption?.value === "" || selectedOption?.value === null 
                    ? null 
                    : (selectedOption ? selectedOption.value : null);
                  field.onChange(value);
                  
                  // If required field and default option selected, trigger validation
                  if (isRequired && value === null) {
                    // Clear errors first, then validation will trigger on submit
                    setTimeout(() => {
                      // Trigger validation by touching the field
                      field.onBlur();
                    }, 0);
                  }
                }
                clearErrors(name);
                dispatch(clearFieldErrors());
              }}
              value={
                isMulti
                  ? enhancedOptions?.filter((option) =>
                      field.value?.includes(option.value)
                    ) || []
                  : field.value === null || field.value === undefined || field.value === ""
                    ? enhancedOptions?.find((option) => option.value === "" || option.value === null) || null
                    : enhancedOptions?.find((option) => option.value === field.value) || null
              }
              className="basic-multi-select"
              classNamePrefix="select"
              {...props}
            />
          </>
        )}
      />
        </div>
      </div>
      {errorMessage && (
        <p className={`text-red-500 text-sm mt-2.5 ${shouldShowLabelLeft ? "ml-[calc(120px+0.75rem)]" : ""}`}>
          {errorMessage}
        </p>
      )}
    </div>
  );
}
DropdownField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  clearErrors: PropTypes.func.isRequired,
  className: PropTypes.string,
  isMulti: PropTypes.bool,
  maxAllow: PropTypes.number,
  register: PropTypes.func,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  defaultOptionLabel: PropTypes.string,
  labelPosition: PropTypes.oneOf(["top", "left", "placeholder"]),
  fieldHeight: PropTypes.oneOf(["small", "medium", "large"]),
  fieldWidth: PropTypes.oneOf(["small", "medium", "large", undefined]),
};

export default DropdownField;
